import { useAtomCallback } from 'jotai/utils';
import { debounce } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { FundFieldOverrideKey } from '../../../schemas/FundFieldOverride.schema';
import { bulkOverrideFundFields } from '../../../services/queries/MaggieFundQueries';
import { getPlainDateString } from '../../../services/queries/MaggieMetricsQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { selectedDateFPState, selectedFundIdStateFP } from '../state/FPState';
import { useInvalidateFundMetrics } from '../Waterfall/useInvalidateFundMetrics';
import { isInvalidNumber } from '../../KPI/components/KPIRequests/KPIRequestResponse/KPIAgGrid';

export function useLpNavOverride() {
  const { pushErrorToast } = useToastMessageState();
  const invalidateFundMetrics = useInvalidateFundMetrics();

  const overrideLpNav = useAtomCallback(
    useCallback(
      async (get, set, newValue: number) => {
        const fundId = get(selectedFundIdStateFP);
        const date = getPlainDateString(get(selectedDateFPState));
        if (!fundId || !date || isInvalidNumber(newValue)) {
          return;
        }
        try {
          await bulkOverrideFundFields([
            {
              date,
              fundId,
              key: FundFieldOverrideKey.LpNav,
              value: String(newValue),
              type: 'number',
            },
          ]);
          invalidateFundMetrics({ date, fundId });
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: getErrorMessage(err, 'Failed to override LP NAV') });
        }
      },
      [pushErrorToast, invalidateFundMetrics]
    )
  );

  return useMemo(() => debounce(overrideLpNav, 500), [overrideLpNav]);
}
